import React, { useState, useEffect, useRef, useMemo } from 'react';
import Colors from '../utilities/Colors';
import { useGlobalHoveredId, useGlobalSelectedId, useGlobalActiveGeography, useGlobalActivePage } from '../../data/StatusStore';
import { d2gRound, getTooltipGeoText } from '../utilities/Utilities';
import { siteConfig } from '../../config/siteConfig';
import context from 'react-bootstrap/esm/AccordionContext';

interface DataItem {
  id: string;
  value: any;
}

export interface HistogramDynamicProps {
  dataArray: DataItem[];
  histMax: number;
  histNA: number;
  histLength: number;
  chartId: string;
  height: string;
  width: string;
  paddingLeft: string;
  paddingRight: string;
  tooltips: boolean;
  DESCRIPTOR: string;
  nycAvg?: number;
  boroByName?: any;
  useCase: string;
  data:any;
}

export const HistogramDynamic: React.FC<HistogramDynamicProps> = ({
  dataArray,
  histMax,
  histNA,
  histLength,
  chartId,
  height,
  width,
  paddingRight,
  paddingLeft,
  tooltips,
  DESCRIPTOR,
  nycAvg,
  boroByName,
  useCase,
  data
}) => {

  //console.log("B042425 data", data);
  //console.log("B042425 dataArray", dataArray);
  const svgRef = useRef<SVGSVGElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const hoveredId = useGlobalHoveredId();
  const selectedId = useGlobalSelectedId();
  const activeGeography = useGlobalActiveGeography();
  const activePage = useGlobalActivePage();

  useEffect(() => {
    const updateContainerWidth = () => {
      if (!svgRef.current) return;
      const nextWidth = svgRef.current.getBoundingClientRect().width;
      setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    };

    let raf1 = 0;
    let raf2 = 0;
    const measureAfterLayout = () => {
      updateContainerWidth();
      raf1 = window.requestAnimationFrame(() => {
        updateContainerWidth();
        raf2 = window.requestAnimationFrame(updateContainerWidth);
      });
    };

    const resizeObserver = new ResizeObserver(measureAfterLayout);
    if (svgRef.current) {
      resizeObserver.observe(svgRef.current);
    }

    window.addEventListener('resize', measureAfterLayout);
    measureAfterLayout();

    return () => {
      window.removeEventListener('resize', measureAfterLayout);
      resizeObserver.disconnect();
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [activeGeography, dataArray?.length, histLength]);

  /*useEffect(() => {
    console.log("CCC041925 DESCRIPTOR", DESCRIPTOR);
  }, [DESCRIPTOR]);
  useEffect(() => {
    console.log("CCCC041925 DESCRIPTOR", DESCRIPTOR);
  }, []);*/

  const viewBoxWidth = histLength * 10; // Assume each bar occupies "10" units in viewBox coordinates for consistency

  // Define the bar width in viewBox units for consistency
  const barWidthViewBoxUnits = dataArray
    ? dataArray.length > 263 
      ? 27 
      : 9
    : 27; // Adjust based on number of bars

  const barWidthAdjustment = dataArray
    ? dataArray.length > 263 
      ? 13 
      : 0
    : 13; // Adjust based on number of bars

  const _height = parseFloat(height);
  const isSbcContext = siteConfig.ctx === "sbc";

  // Shared bubble sizing: shrink all bubbles uniformly when collisions exist
  const bubbleConfig = useMemo(() => {
    const cfg = { bubbleSize: 23, collisionGap: 24, verticalStep: 12 };
    if (isSbcContext || activeGeography === "Boro" || !boroByName || !dataArray || dataArray.length === 0) return cfg;

    const unit = containerWidth / dataArray.length;
    const boroAdjust = activeGeography === "Boro" ? unit / 20 : 0;

    // NYC left position for collision detection
    let nycLeftPx: number | null = null;
    if (nycAvg && nycAvg > 0) {
      let nycIndex = dataArray.findIndex((d, i) => {
        return d.value !== "NA" && nycAvg >= d.value && dataArray[i + 1] && nycAvg < dataArray[i + 1].value;
      });
      if (nycIndex === -1) {
        nycIndex = dataArray.findIndex((d, i) => {
          return d.value !== "NA" && dataArray[i - 1]?.value === "NA";
        });
      }
      if (nycIndex === -1) {
        if (dataArray[dataArray.length - 1].value < nycAvg) nycIndex = dataArray.length - 1;
        else if (dataArray[0].value > nycAvg) nycIndex = 0;
      }
      if (nycIndex >= 0) {
        const _nycIndex = nycAvg === dataArray[nycIndex].value ? nycIndex : (nycAvg < dataArray[nycIndex].value ? nycIndex - 0.5 : nycIndex + 0.5);
        nycLeftPx = (_nycIndex * unit) - 11.5 + (unit / 2) - boroAdjust;
      }
    }

    // Borough left positions
    const lefts: number[] = [];
    Object.keys(boroByName).forEach((name) => {
      const raw = boroByName[name]?.value;
      const avg = Number.isFinite(raw) ? raw : NaN;
      if (!Number.isFinite(avg)) return;
      let idx = dataArray.findIndex((d, i) => d.value !== "NA" && avg >= d.value && dataArray[i + 1] && avg < dataArray[i + 1].value);
      if (idx === -1) idx = dataArray.findIndex((d, i) => d.value !== "NA" && dataArray[i - 1]?.value === "NA");
      if (idx === -1) {
        if (dataArray[dataArray.length - 1].value < avg) idx = dataArray.length - 1;
        else if (dataArray[0].value > avg) idx = 0;
        else return;
      }
      const _idx = avg === dataArray[idx].value ? idx : (avg < dataArray[idx].value ? idx - 0.5 : idx + 0.5);
      const leftPx = (_idx * unit) - 11.5 + (unit / 2) - boroAdjust;
      lefts.push(leftPx);
    });

    lefts.sort((a, b) => a - b);
    let collision = false;
    for (let i = 1; i < lefts.length; i++) {
      if (Math.abs(lefts[i] - lefts[i - 1]) < cfg.collisionGap) { collision = true; break; }
    }
    if (!collision && nycLeftPx !== null) {
      for (const lp of lefts) { if (Math.abs(lp - nycLeftPx) < cfg.collisionGap) { collision = true; break; } }
    }

    return { ...cfg, bubbleSize: collision ? 20 : 23 };
  }, [boroByName, dataArray, containerWidth, activeGeography, nycAvg, isSbcContext]);

  // ---------------------------------------------------------------------
  // Compute the NYC bubble overlay once (always rendered, behind tooltips)
  // This bubble is fixed at 23x23 px.
  
  const nycBubble = useMemo(() => {
    if (!DESCRIPTOR || (!DESCRIPTOR.includes("%") && !DESCRIPTOR.includes("Rate"))) return null;
    if (!nycAvg || nycAvg <= 0 || !dataArray || dataArray.length === 0) return null;
    // Find the bar where nycAvg fits: first bar with value >= nycAvg
    let nycIndex = dataArray.findIndex((d, i) => {
      return d.value !== "NA" && nycAvg >= d.value && dataArray[i + 1] && nycAvg < dataArray[i + 1].value;
    });
    if (nycIndex === -1) {
      nycIndex = dataArray.findIndex((d, i) => {
        return d.value !== "NA" && dataArray[i - 1]?.value === "NA";
      });
    }
    let skipNyc = false;
    if (nycIndex === -1) {
      // If not found, set to last index
      //console.log("C050625 nycAvg", nycAvg) 
      //console.log("C050625 dataArray", dataArray) 
      //console.log("C050625 nycIndex", nycIndex) 
      if (dataArray[dataArray.length - 1].value < nycAvg) {
        nycIndex = dataArray.length - 1;
      }else if (dataArray[0].value > nycAvg){
        nycIndex = 0;
      }else{
        skipNyc = true;
      }
    }
    // Compute left position:
    // Use similar formula as your tooltip left calculation,
    // but center the bubble using a fixed base unit (each bar is 10 units).

    let _nycIndex = nycIndex >= 0 
      ? nycAvg === dataArray[nycIndex].value 
        ? nycIndex // Keep it centered between bars
        : nycAvg < dataArray[nycIndex].value 
          ? nycIndex - 0.5 // Offset to left of bar (rare cases where the NYC avg is wrong)
          : nycIndex + 0.5 // Offset to right of bar (most cases)
      : 0; // Default to 0 if nycAvg is not valid
    //console.log("C050625 _nycIndex", _nycIndex);
          
    const bubbleLeft = `calc(${(_nycIndex * (100 / dataArray.length))}% - 11.5px + ${(containerWidth / dataArray.length) / 2}px - ${activeGeography === "Boro" ? (containerWidth / dataArray.length) / 20 : 0}px)`;
    // Vertical position: fixed 23px bubble at bottom of histogram.
    const bubbleTop = `${_height - 18}px`;
    const size = bubbleConfig.bubbleSize;
    const labelH = Math.round(size * 13 / 23);
    const pointerH = Math.round(size * 7.5 / 23);
    const pointerW = Math.round(size * 5 / 23);
    return (
      skipNyc ? null : // Skip rendering if nycAvg is not in range
      <div
        key="nycBubble"
        style={{
          position: "absolute",
          width: `${size}px`,
          height: `${size}px`,
          left: bubbleLeft,
          top: bubbleTop,
          zIndex: 1, // behind tooltips which have higher z-index
          pointerEvents: "none"
        }}
      >
        <div
          style={{
            position: "relative",
            width: `${size}px`,
            height: `${labelH}px`,
            backgroundColor: "#298AFF",
            clipPath: "path('M 0 100% L 0 35% Q 0 0 35% 0 L 65% 0 Q 100% 0 100% 35% L 100% 100% Z')",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "9px",
            fontWeight: "bold",
            color: "#ffffff",
            borderRadius: "4px",
            paddingTop: "0px",
            paddingBottom: "1px",
          }}
        >
          {siteConfig.nav.overallLabel ?? 'NYC'}
          <div
            style={{
              position: "absolute",
              top: `-${pointerH}px`,
              left: `calc(50% - ${pointerW}px)`,
              width: "0",
              height: "0",
              borderLeft: `${pointerW}px solid transparent`,
              borderRight: `${pointerW}px solid transparent`,
              borderBottom: `${pointerH}px solid #298AFF`,
            }}
          ></div>
        </div>
      </div>
    );
  }, [nycAvg, dataArray, containerWidth, activeGeography, _height, DESCRIPTOR]);


  const boroBubbles = useMemo(() => {
    if (isSbcContext) return null;
    if (activeGeography === "Boro") return null;
    if (!boroByName || !dataArray || dataArray.length === 0) return null;
    // Only show borough bubbles for percent or rate indicators
    if (!DESCRIPTOR || (!DESCRIPTOR.includes("%") && !DESCRIPTOR.includes("Rate"))) return null;
    let lookup:any = {
      "Bronx": "BX",
      "Brooklyn": "BK",
      "Manhattan": "MN",
      "Queens": "QN",
      "Staten Island": "SI",
    }
    //
    // Pre-compute NYC bubble's left position (in px) for collision checks
    // so we can avoid overlapping boro bubbles with NYC.
    let nycLeftPx: number | null = null;
    if (nycAvg && nycAvg > 0 && dataArray && dataArray.length > 0) {
      let nycIndex = dataArray.findIndex((d, i) => {
        return d.value !== "NA" && nycAvg >= d.value && dataArray[i + 1] && nycAvg < dataArray[i + 1].value;
      });
      if (nycIndex === -1) {
        nycIndex = dataArray.findIndex((d, i) => {
          return d.value !== "NA" && dataArray[i - 1]?.value === "NA";
        });
      }
      if (nycIndex === -1) {
        if (dataArray[dataArray.length - 1].value < nycAvg) {
          nycIndex = dataArray.length - 1;
        } else if (dataArray[0].value > nycAvg) {
          nycIndex = 0;
        }
      }
      if (nycIndex >= 0) {
        const _nycIndex = nycAvg === dataArray[nycIndex].value
          ? nycIndex
          : (nycAvg < dataArray[nycIndex].value ? nycIndex - 0.5 : nycIndex + 0.5);
        const unit = containerWidth / dataArray.length;
        const boroAdjust = activeGeography === "Boro" ? unit / 20 : 0;
        nycLeftPx = (_nycIndex * unit) - 11.5 + (unit / 2) - boroAdjust;
      }
    }

    // Build raw items first so we can sort by x and place in minimal levels
    const items: Array<{
      name: string;
      leftPx: number;
      leftCss: string;
    }> = [];

    Object.keys(boroByName).forEach((name) => {
      const raw = boroByName[name]?.value;
      const avg = Number.isFinite(raw) ? raw : NaN;
      if (!Number.isFinite(avg)) return;

      let idx = dataArray.findIndex((d, i) => {
        return d.value !== "NA" && avg >= d.value && dataArray[i + 1] && avg < dataArray[i + 1].value;
      });
      if (idx === -1) {
        idx = dataArray.findIndex((d, i) => d.value !== "NA" && dataArray[i - 1]?.value === "NA");
      }

      let render = true;
      if (idx === -1) {
        if (dataArray[dataArray.length - 1].value < avg) {
          idx = dataArray.length - 1;
        } else if (dataArray[0].value > avg) {
          idx = 0;
        } else {
          render = false;
        }
      }
      if (!render) return;

      const _idx = idx >= 0
        ? (avg === dataArray[idx].value ? idx : (avg < dataArray[idx].value ? idx - 0.5 : idx + 0.5))
        : 0;
      console.log("011226 _idx for", name, avg, _idx);
      const unit = containerWidth / dataArray.length;
      const boroAdjust = activeGeography === "Boro" ? unit / 20 : 0;
      const leftPx = (_idx * unit) - 11.5 + (unit / 2) - boroAdjust;
      const leftCss = `calc(${(_idx * (100 / dataArray.length))}% - 11.5px + ${(containerWidth / dataArray.length) / 2}px - ${activeGeography === "Boro" ? (containerWidth / dataArray.length) / 20 : 0}px)`;

      items.push({ name, leftPx, leftCss });
    });

    // Sort by x so we place left-to-right, minimizing vertical levels
    items.sort((a, b) => a.leftPx - b.leftPx);

    // Levels: each level tracks placed left positions for collision checks
    const levels: number[][] = [];
    const collisionGap = bubbleConfig.collisionGap; // ~bubble width; avoid touching
    const verticalStep = bubbleConfig.verticalStep; // per-level raise

    return items.map(({ name, leftPx, leftCss }) => {
      let level = 0;
      while (true) {
        if (!levels[level]) levels[level] = [];
        const collides = levels[level].some((pl) => Math.abs(pl - leftPx) < collisionGap)
          || (level === 0 && nycLeftPx !== null && Math.abs(nycLeftPx - leftPx) < collisionGap);
        if (!collides) {
          levels[level].push(leftPx);
          break;
        }
        level += 1;
      }

      //const top = `${_height - 18 - (level * verticalStep)}px`;
      const top = `${_height - 18}px`;

      // Uniform bubble size based on shared config (consistent across all boxes)
      const bubbleSize = bubbleConfig.bubbleSize;
      const labelHeight = Math.round(bubbleSize * 13 / 23);
      const pointerH = Math.round(bubbleSize * 7.5 / 23);
      const pointerW = Math.round(bubbleSize * 5 / 23);

      return (
        <div
          key={`boroBubble_${name}`}
          style={{
            position: "absolute",
            width: `${bubbleSize}px`,
            height: `${bubbleSize}px`,
            left: leftCss,
            top,
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              position: "relative",
              width: `${bubbleSize}px`,
              height: `${labelHeight}px`,
              backgroundColor: "#8bbefeff",
              clipPath: "path('M 0 100% L 0 35% Q 0 0 35% 0 L 65% 0 Q 100% 0 100% 35% L 100% 100% Z')",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "9px",
              fontWeight: "bold",
              color: "#ffffff",
              borderRadius: "4px",
              paddingTop: "0px",
              paddingBottom: "1px",
            }}
          >
            {lookup[name]}
            <div
              style={{
                position: "absolute",
                top: `-${pointerH}px`,
                left: `calc(50% - ${pointerW}px)`,
                width: "0",
                height: "0",
                borderLeft: `${pointerW}px solid transparent`,
                borderRight: `${pointerW}px solid transparent`,
                borderBottom: `${pointerH}px solid #8bbefeff`,
              }}
            ></div>
          </div>
        </div>
      );
    });
  }, [boroByName, dataArray, containerWidth, activeGeography, _height, isSbcContext, DESCRIPTOR]);

  return (
    // Wrap everything in a relative container to position the bubble overlay
    <div style={{ position: "relative" }}>
      {dataArray && dataArray.length > 0 ? (
        <>
          {dataArray.map((d, i) => {
            //console.log("B122825 d", d)
            const isSelected = d.id == selectedId;
            const isHovered = d.id == hoveredId;
            const fill = isSelected ? Colors.highlightSelect : isHovered ? Colors.highlightHover : 'none'; // 'none' for transparent or any default non-highlight color
            
            if (!isSelected && !isHovered) return null; // Skip rendering if not selected or hovered

            const barHeight = d.value === "NA" ? 0 : (d.value / histMax) * _height; // Adjusted for viewBox height of 80 units
            //console.log("H093024 dataArray", dataArray);
            //console.log("102224 dataArray.length", dataArray.length);
            //console.log("102224 containerWidth", containerWidth);
            //console.log("102224 activeGeography", activeGeography);
            //console.log("102224 i", i);
            //console.log("CC041925 DESCRIPTOR", DESCRIPTOR);
            return (
              <div
                key={`${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}_box_hist_cont`}
                /*style={{
                  paddingRight: paddingRight,
                  paddingLeft: paddingLeft,
                }}*/
                style={{ position: "relative" }}
              >
                <div
                  id={`${isSelected ? 'selected' : 'hovered'}HistogramTooltip`}
                  className={`hist-label-rect ${isSelected ? 'popup-selected' : 'popup-hovered'}`}
                  key={`${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}_box_hist`}
                  style={{
                    width: useCase === "race and gender" ? "66px" : "80px",
                    height: useCase === "race and gender" ? "32px" : "50px",
                    backgroundColor: "#FAF5F5",
                    border: `1px solid ${fill}`,
                    color: `${fill}`,
                    position: "absolute",
                    fontSize: "12px",
                    textAlign: "center",
                    pointerEvents: "none",
                    left: useCase === "race and gender" 
                      ? `calc(${(i * (100 / dataArray.length))}% - 33px + ${(containerWidth / dataArray.length) / 2}px - ${activeGeography == "Boro" ? (containerWidth / dataArray.length) / 20 : 0}px)`
                      : `calc(${(i * (100 / dataArray.length))}% - 40px + ${(containerWidth / dataArray.length) / 2}px - ${activeGeography == "Boro" ? (containerWidth / dataArray.length) / 20 : 0}px)`,
                    display: `${tooltips ? 'block' : 'none'}`,
                    lineHeight: 1.25,
                    paddingTop: activeGeography === "Tract" || (siteConfig.ctx === "sbc" && activeGeography === "CD")
                      ? "0px" 
                      : useCase === "race and gender" 
                        ? "0px" 
                        : "8.5px",
                    borderRadius: '4px',
                  }}
                >
                  <div
                    style={{
                      zIndex: 10,
                      position: "relative",
                      fontSize: `${activeGeography !== "NTA" ? "12px" : "10px"}`,
                    }}
                  >
                    {getTooltipGeoText(data.dataJSON, activeGeography, d.id)}<br/><b>{d2gRound(d.value, { DESCRIPTOR: DESCRIPTOR })}</b>
                  </div>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: "#FAF5F5",
                      border: `1px solid ${fill}`,
                      position: "absolute",
                      bottom: "-8.5px",
                      left: "calc(50% - 8px)",
                      rotate: "45deg",
                      borderTop: "none",
                      borderLeft: "none",
                      zIndex: 9,
                    }}
                  ></div>
                </div>
                <svg
                  id={`${isSelected ? 'selected' : 'hovered'}HistogramSelectionBar`}
                  key={`${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}_svg_hist`}
                  ref={svgRef}
                  width={width}
                  height={`${height}px`}
                  viewBox={`0 0 ${viewBoxWidth} ${height}`}
                  preserveAspectRatio="none"
                  className="hist-container-dynamic"
                  style={{
                    paddingRight: paddingRight,
                    paddingLeft: paddingLeft,
                  }}
                >
                  <rect
                    key={`${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}_rect`}
                    x={(i * 10) - barWidthAdjustment} // Each bar starts at 10 units apart
                    y={_height - barHeight} // Adjust based on barHeight for bottom-up growth
                    width={barWidthViewBoxUnits} // Consistent bar width
                    height={barHeight}
                    fill={fill}
                    className="hist-bar"
                  />
                </svg>
              </div>
            );
          })}
          {/* Render the NYC bubble overlay (always visible, behind tooltips) */}
          {boroBubbles}
          {nycBubble}
        </>
      ) : null}
    </div>
  );
};

export default HistogramDynamic;
